import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Loader2, LogOut, MessageSquare, ShieldCheck } from 'lucide-react';
import api from '@/lib/axios';
import convApi, { clearCsrf, setCsrf } from '@/lib/conversations';
import { useSeo } from '../hooks/useSeo';
import MarketplaceFooter from '@/components/marketplace/MarketplaceFooter';
import ConversationThread from '@/components/marketplace/ConversationThread';

const ESTADO_LABEL = {
  aberta: 'Aberta', resolvida: 'Resolvida', encerrada: 'Encerrada',
  bloqueada_moderacao: 'Encerrada pela moderação',
};

const quando = (iso) => (iso ? new Date(iso).toLocaleString('pt-PT', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '');

function AccessBox({ onSession }) {
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [step, setStep] = useState('email');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  const sendCode = async () => {
    setBusy(true); setErr(''); setMsg('');
    try {
      await api.post('/marketplace/verificacao/enviar-codigo', { email: email.trim() });
      setStep('code');
      setMsg('Enviámos um código de 6 dígitos para o seu email.');
    } catch (e) { setErr(e.response?.data?.detail || 'Não foi possível enviar o código.'); }
    finally { setBusy(false); }
  };

  const confirm = async () => {
    setBusy(true); setErr('');
    try {
      const v = await api.post('/marketplace/verificacao/confirmar', { email: email.trim(), code: code.trim() });
      const res = await convApi.post('/sessao/email', { email: email.trim(), verification_token: v.data.verification_token });
      setCsrf(res.data.csrf);
      onSession();
    } catch (e) { setErr(e.response?.data?.detail || 'Código inválido.'); }
    finally { setBusy(false); }
  };

  const pedirLigacao = async () => {
    setBusy(true); setErr('');
    try {
      const res = await convApi.post('/pedir-ligacao', { email: email.trim() });
      setMsg(res.data?.mensagem || 'Se existirem conversas, enviámos uma ligação de acesso.');
    } catch (e) { setErr(e.response?.data?.detail || 'Não foi possível enviar a ligação.'); }
    finally { setBusy(false); }
  };

  return (
    <div className="mx-auto max-w-md rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="conv-access-box">
      <h2 className="text-base font-bold text-slate-900">As minhas conversas</h2>
      <p className="mt-1 text-xs text-slate-500">
        Sem conta e sem password: confirme o seu email e veja apenas as conversas associadas a esse email.
      </p>
      <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
        className="mt-3 w-full rounded-xl border border-slate-300 px-3 py-2 text-sm" placeholder="O seu email"
        data-testid="conv-access-email" />
      {step === 'email' ? (
        <button type="button" onClick={sendCode} disabled={busy || !email.trim()}
          className="mt-3 w-full rounded-xl bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-60"
          data-testid="conv-access-send">{busy ? 'A enviar...' : 'Enviar código'}</button>
      ) : (
        <>
          <input value={code} onChange={(e) => setCode(e.target.value)}
            className="mt-3 w-full rounded-xl border border-slate-300 px-3 py-2 text-sm" placeholder="Código de 6 dígitos"
            data-testid="conv-access-code" />
          <button type="button" onClick={confirm} disabled={busy || !code.trim()}
            className="mt-3 w-full rounded-xl bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-60"
            data-testid="conv-access-confirm">{busy ? 'A validar...' : 'Entrar nas minhas conversas'}</button>
        </>
      )}
      <button type="button" onClick={pedirLigacao} disabled={busy || !email.trim()}
        className="mt-2 w-full rounded-xl border border-slate-300 px-4 py-2 text-xs font-semibold text-slate-600"
        data-testid="conv-access-link">Prefiro receber uma ligação de acesso por email</button>
      {msg && <p className="mt-3 text-xs font-semibold text-emerald-700" data-testid="conv-access-msg">{msg}</p>}
      {err && <p className="mt-3 text-xs font-semibold text-red-600" data-testid="conv-access-error">{err}</p>}
      <p className="mt-4 inline-flex items-start gap-1.5 text-[11px] text-slate-500">
        <ShieldCheck className="mt-0.5 h-3.5 w-3.5 text-emerald-600" />
        Isto não é uma conta: é apenas um acesso temporário neste dispositivo, que pode terminar quando quiser.
      </p>
    </div>
  );
}

export default function MarketplaceConversationsPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ativo, setAtivo] = useState(true);

  useSeo({
    title: 'As minhas conversas · Marketplace TVDE',
    description: 'Conversas privadas entre interessados e anunciantes do Marketplace TVDE.',
    noindex: true,
  });

  const loadList = useCallback(async () => {
    try {
      const res = await convApi.get('/minhas');
      setItems(res.data.items || []);
    } catch (_) {}
  }, []);

  const boot = useCallback(async () => {
    setLoading(true);
    try {
      const estado = await convApi.get('/estado');
      if (!estado.data?.ativo) { setAtivo(false); setLoading(false); return; }
      const res = await convApi.get('/sessao/atual');
      setCsrf(res.data.csrf);
      setSession(res.data);
      await loadList();
    } catch (_) {
      setSession(null);
      clearCsrf();
    } finally { setLoading(false); }
  }, [loadList]);

  useEffect(() => { boot(); }, [boot]);

  const sair = async () => {
    try { await convApi.post('/sessao/sair', {}); } catch (_) {}
    clearCsrf(); setSession(null); setItems([]);
    navigate('/conversas');
  };

  const revogar = async () => {
    try { await convApi.post('/sessao/revogar-todas', {}); } catch (_) {}
    clearCsrf(); setSession(null); setItems([]);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-4xl items-center justify-between gap-3 px-4 py-3">
          <Link to="/" className="text-sm font-black text-slate-900">Marketplace <span className="text-emerald-600">TVDE</span></Link>
          {session && (
            <div className="flex items-center gap-2">
              <span className="hidden text-[11px] text-slate-500 sm:inline" data-testid="conv-session-email">{session.email}</span>
              <button type="button" onClick={sair} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-2.5 py-1.5 text-xs font-semibold text-slate-600" data-testid="conv-logout">
                <LogOut className="h-3.5 w-3.5" /> Terminar sessão
              </button>
            </div>
          )}
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-4 py-6">
        {loading && (
          <p className="flex items-center gap-2 text-sm text-slate-500" data-testid="conv-loading">
            <Loader2 className="h-4 w-4 animate-spin" /> A carregar...
          </p>
        )}

        {!loading && !ativo && (
          <p className="rounded-2xl border border-slate-200 bg-white p-6 text-sm text-slate-600" data-testid="conv-disabled">
            As conversas internas ainda não estão disponíveis.
          </p>
        )}

        {!loading && ativo && !session && <AccessBox onSession={boot} />}

        {!loading && ativo && session && id && (
          <ConversationThread conversationId={id} onSessionLost={() => { clearCsrf(); setSession(null); }} />
        )}

        {!loading && ativo && session && !id && (
          <div data-testid="conv-list">
            <h1 className="text-2xl font-black text-slate-900 sm:text-3xl">As minhas conversas</h1>
            <p className="mt-1 text-sm text-slate-500">Só vê conversas associadas ao email verificado nesta sessão.</p>
            {items.length === 0 ? (
              <div className="mt-6 rounded-2xl border border-dashed border-slate-300 bg-white p-8 text-center" data-testid="conv-list-empty">
                <MessageSquare className="mx-auto h-8 w-8 text-slate-300" />
                <p className="mt-2 text-sm font-semibold text-slate-700">Ainda não tem conversas</p>
                <p className="mt-1 text-xs text-slate-500">Envie uma mensagem num anúncio para começar.</p>
                <Link to="/" className="mt-3 inline-block rounded-xl bg-emerald-600 px-4 py-2 text-sm font-semibold text-white">Ver anúncios</Link>
              </div>
            ) : (
              <ul className="mt-5 space-y-2">
                {items.map((c) => (
                  <li key={c.id}>
                    <Link to={`/conversas/${c.id}`} className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white p-3 transition hover:shadow-md" data-testid={`conv-item-${c.id}`}>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-bold text-slate-900">{c.anuncio?.titulo}</p>
                        <p className="mt-0.5 truncate text-xs text-slate-500">
                          {c.outro?.nome_publico} · {c.papel === 'anunciante' ? 'Você é o anunciante' : 'Você contactou'} · {ESTADO_LABEL[c.estado] || c.estado}
                        </p>
                        <p className="mt-0.5 text-[11px] text-slate-400">{quando(c.last_message_at)} · {c.mensagens} mensagens</p>
                      </div>
                      {c.nao_lidas > 0 && (
                        <span className="rounded-full bg-emerald-600 px-2 py-0.5 text-[11px] font-bold text-white" data-testid={`conv-unread-${c.id}`}>{c.nao_lidas}</span>
                      )}
                    </Link>
                  </li>
                ))}
              </ul>
            )}
            <button type="button" onClick={revogar} className="mt-6 text-[11px] font-semibold text-slate-500 underline" data-testid="conv-revoke-all">
              Terminar sessão em todos os dispositivos
            </button>
          </div>
        )}
      </main>
      <MarketplaceFooter />
    </div>
  );
}
